import { gql } from '@apollo/client';
import React, { useReducer, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import isTouchDevice from 'is-touch-device';
import { Badge, Clickable } from '@moda/om';
import { VERTICALS, Vertical } from '../../../../constants';
import { hrefFor } from '../../../../routers';
import { Link } from '../../../Link';
import { Favorite } from '../../../Favorite';
import { tc } from '../../../../lib/trackingContext';
import { getActiveVariantBy } from '../../../../lib/getActiveVariantBy';
import { VariantCellFragment } from '../../../../generated/types/VariantCellFragment';
import { PriceCollectionBadge } from '../../../CollectionBadge';
import {
  VariantCellImageGallery,
  VARIANT_CELL_IMAGE_GALLERY_FRAGMENT
} from './VariantCellImageGallery';
import {
  VariantCellProductPrice,
  VARIANT_CELL_PRODUCT_PRICE_FRAGMENT
} from './VariantCellProductPrice';
import { VariantCellCallToAction } from './VariantCellCallToAction';
import { VariantCellQuickAdd, VARIANT_CELL_QUICK_ADD_FRAGMENT } from './VariantCellQuickAdd';
import { VariantCellColorSelector, VARIANT_CELL_COLOR_SELECTOR } from './VariantCellColorSelector';

import './VariantCell.scss';

export const VARIANT_CELL_VARIANT_FRAGMENT = gql`
  fragment VariantCellVariantFragment on VariantData {
    id
    collections
    href
    label
  }
`;

export const VARIANT_CELL_FRAGMENT = gql`
  fragment VariantCellFragment on Variant {
    id
    name
    gender
    productId: product_id
    designerName: designer_name
    designerSlug: designer_slug
    availability
    customizable
    tag
    productSlug: slug
    masterVariant: master_variants_data {
      id
      ...VariantCellVariantFragment
    }
    otherVariants: other_variants_data {
      id
      ...VariantCellVariantFragment
    }
    ...VariantCellProductPriceFragment
    ...VariantCellImageGalleryFragment
    ...VariantCellQuickAddFragment
    ...VariantCellColorSelectorFragment
  }
  ${VARIANT_CELL_VARIANT_FRAGMENT}
  ${VARIANT_CELL_PRODUCT_PRICE_FRAGMENT}
  ${VARIANT_CELL_IMAGE_GALLERY_FRAGMENT}
  ${VARIANT_CELL_QUICK_ADD_FRAGMENT}
  ${VARIANT_CELL_COLOR_SELECTOR}
`;

const COLOR_SELECT_DELAY = 100;

enum Mode {
  Resting,
  Active,
  Add,
  Colors
}
interface State {
  mode: Mode;
  activeVariantId: string;
}

type Action =
  | { type: 'SET_ACTIVE_VARIANT_ID'; payload: { id: string } }
  | { type: 'SET_MODE'; payload: { mode: Mode } };

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  variant: VariantCellFragment;
  removeUnfilteredSizes?: boolean;
  quickAddToBag?: boolean;
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.payload.mode };
    case 'SET_ACTIVE_VARIANT_ID':
      return { ...state, activeVariantId: action.payload.id };
    default:
      return state;
  }
};

export const VariantCell: React.FC<Props> = React.memo(({ className, variant, ...rest }) => {
  const [state, dispatch] = useReducer(reducer, {
    mode: Mode.Resting,
    activeVariantId: variant.masterVariant.id
  });

  const vertical = VERTICALS[variant.gender as Vertical];

  const activeVariant = useMemo(() => getActiveVariantBy(variant, 'id', state.activeVariantId), [
    variant,
    state.activeVariantId
  ]);

  const handleSelectColor = useCallback(
    (color: string) => {
      const { id } = getActiveVariantBy(variant, 'color', color);
      dispatch({ type: 'SET_ACTIVE_VARIANT_ID', payload: { id } });
      setTimeout(
        () => dispatch({ type: 'SET_MODE', payload: { mode: Mode.Active } }),
        COLOR_SELECT_DELAY
      );
    },
    [variant]
  );

  const handleMouseEnter = useCallback(() => {
    if (isTouchDevice()) return;

    if (state.mode === Mode.Add) return;

    dispatch({ type: 'SET_MODE', payload: { mode: Mode.Active } });
  }, [state.mode]);

  const handleMouseLeave = useCallback(() => {
    if (isTouchDevice()) return;

    dispatch({ type: 'SET_MODE', payload: { mode: Mode.Resting } });
  }, []);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    switch (event.currentTarget.name) {
      case 'add':
        return dispatch({ type: 'SET_MODE', payload: { mode: Mode.Add } });
      case 'colors':
        return dispatch({ type: 'SET_MODE', payload: { mode: Mode.Colors } });
      case 'active':
        return dispatch({ type: 'SET_MODE', payload: { mode: Mode.Active } });
      default:
        break;
    }
  }, []);

  const trackHrefClick = useCallback(() => {
    tc.track('Product Clicked', { addToScopeOnce: { variant, activeVariant } });
  }, [variant, activeVariant]);

  const href = hrefFor.ProductDetailPage({
    vertical,
    designerSlug: variant.designerSlug || '',
    productSlug: variant.productSlug,
    variantId: activeVariant.id
  });

  return (
    <div
      className={classnames('VariantCell', className, {
        'VariantCell--active': state.mode !== Mode.Resting
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-variant-pk={variant.id}
      data-product-id={variant.productId}
      {...rest}
    >
      <div className="VariantCell__wrapper">
        <div className="VariantCell__content">
          <Link
            className="VariantCell__image"
            to={href}
            rel="nofollow noindex"
            onClick={trackHrefClick}
          >
            <VariantCellImageGallery
              key={activeVariant.id}
              variant={variant}
              activeVariantId={activeVariant.id}
              active={state.mode !== Mode.Resting}
            />
          </Link>

          {state.mode !== Mode.Resting && (
            <div className="VariantCell__actions">
              {state.mode === Mode.Active && (
                <VariantCellCallToAction
                  onClickQuickView={handleClick}
                  variant={variant}
                  activeVariantId={activeVariant.id}
                  href={href}
                />
              )}

              {state.mode === Mode.Add && (
                <VariantCellQuickAdd
                  variant={variant}
                  activeVariantId={activeVariant.id}
                  href={href}
                />
              )}

              {state.mode === Mode.Colors && (
                <VariantCellColorSelector
                  variant={variant}
                  activeVariantId={activeVariant.id}
                  onSelectColor={handleSelectColor}
                />
              )}
            </div>
          )}
        </div>

        <Favorite type="ICON" variantId={activeVariant.id} />

        <div className="VariantCell__details">
          <Link
            className="VariantCell__anchor"
            to={href}
            rel="nofollow noindex"
            onClick={trackHrefClick}
          >
            {activeVariant.label ? (
              <div className="VariantCell__availability">{activeVariant.label}</div>
            ) : (
              <div className="VariantCell__availability">&nbsp;</div>
            )}

            <div className="VariantCell__designer-name" title={variant.designerName}>
              {variant.designerName}
            </div>
          </Link>

          {variant.name && (
            <Link className="VariantCell__anchor" to={href} onClick={trackHrefClick}>
              <div className="VariantCell__product-name" title={variant.name}>
                {variant.name}
              </div>
            </Link>
          )}

          <Link
            className="VariantCell__anchor"
            to={href}
            rel="nofollow noindex"
            onClick={trackHrefClick}
          >
            <VariantCellProductPrice variant={variant} activeVariantId={activeVariant.id}>
              <PriceCollectionBadge collections={activeVariant.collections} />
            </VariantCellProductPrice>
          </Link>

          {variant.tag && (
            <div className="VariantCell__badge">
              <Badge>{variant.tag}</Badge>
            </div>
          )}

          {variant.otherVariants.length > 0 && (
            <Clickable
              className="VariantCell__more-colors"
              name={state.mode === Mode.Colors ? 'active' : 'colors'}
              onClick={handleClick}
            >
              {state.mode === Mode.Colors ? '-' : '+'} Colors
            </Clickable>
          )}
        </div>
      </div>
    </div>
  );
});
